/**
 * The accessible name for `SearchableSelect` / `SearchableMultiSelect`'s
 * `role="listbox"` option list (#13479).
 *
 * `listbox` is one of the roles axe marks `accessibleNameRequired`, so an
 * unnamed one is `aria-input-field-name` at SERIOUS impact. The name is the
 * trigger's own label, copied as a STRING — never an `aria-labelledby`
 * pointing at the trigger. `src/components/ui/select.tsx` learned that at
 * #13186: accname step 2E treats the trigger as an embedded control, so
 * recursing into it takes its VALUE rather than its label, and for a
 * select-like control the value is the chosen option's text — which lives
 * inside the panel being named. The computation returns EMPTY in Chromium,
 * and axe does not implement the carve-out, so it reports such a panel as
 * correctly named. Only a real browser sees the difference.
 *
 * Two strings, in this order:
 *
 *  - `ariaLabel` — what the caller explicitly named the trigger.
 *  - `placeholder` — the trigger's own visible content when nothing is
 *    selected, and therefore its accessible name in that state. Both
 *    components default it, so this is reached rather than hypothetical: most
 *    of the nine call sites pass no `ariaLabel`.
 *
 * `||` rather than `??` is deliberate: an empty string is not a name, and
 * `aria-label=""` is the exact violation this function exists to prevent.
 * {@link LISTBOX_FALLBACK_LABEL} is the last resort for a caller that blanked
 * both — a generic name beats none, and beats silently emitting an empty one.
 *
 * It lives in its own module because both components need it identically and
 * they are two files (#13473, `check:file-size-ratchet`) — the same reason
 * `./searchable-select-menu-pos` and `./searchable-select-search-field` do.
 */

/** Last-resort listbox name; see this module's header. */
export const LISTBOX_FALLBACK_LABEL = 'Options'

/**
 * Marks the portal root of an open `SearchableSelect` /
 * `SearchableMultiSelect` popover — the unroled shell that owns the search
 * field, the option list and the footer.
 *
 * It exists because `role="listbox"` moved onto the option list, so
 * `getByRole('listbox')` no longer resolves to anything that contains the
 * search field or the footer. A test that needs the whole popover addresses
 * this attribute instead. Single-sourced here for the same reason
 * {@link listboxAccessibleName} is: both components render it identically and
 * they are two files.
 *
 * Producer-side only. The consumers are test locators (Playwright CSS
 * selectors and a jsdom `closest()` call), where the attribute has to appear
 * inside a selector string literal anyway.
 */
export const SEARCHABLE_SELECT_POPOVER_ATTR = 'data-searchable-select-popover'

export function listboxAccessibleName(
  ariaLabel: string | undefined,
  placeholder: string,
): string {
  return ariaLabel?.trim() || placeholder?.trim() || LISTBOX_FALLBACK_LABEL
}
